import type { ReactNode } from 'react';

import type { B2BAssets, MediaProgramSection } from '../types';

export interface B2BMediaProgramProps {
    section: MediaProgramSection;
    assets: B2BAssets;
}

/**
 * ArchiPro Media Program (Figma `512:24604`, 1920×1995).
 *
 * Layout (1920-frame coordinates):
 *  - Hero photo: `left=25, top=0, w=1870, h=915.94` (rounded rect).
 *  - Right intro paragraph: `left=960, top=1013.5, w=865.66`
 *    25px Medium #000 leading 1.1 (1.302vw).
 *  - Right card grid: `left=960, top=1220.78, w=892.66, h=774.52`
 *    Two columns at x=0 and x=447.1, each w=357.09.
 *    Three rows at row-y=0, 302.42, 603.97; row height 170.55.
 *    Each row has a top divider line; title at y=20.18 (size 20 #333),
 *    body at y=66.55 (size 20 #888 w=357.09 leading 1.3).
 *  - Film cover card: `left=213, top=1133, w=492.61, h=680.62`
 *    white background, drop-shadow, photo content with caption overlays.
 *
 * Eyebrow / title for this block live in the sibling `intro-band` above
 * (Figma `512:24642`, "(ArchiPro Media Program)"); not inlined here.
 *
 * `max-md:` fallback stacks the hero, intro, and card grid vertically.
 */
const FRAME_HEIGHT = 1995;

const pctX = (x: number) => (x / 1920) * 100;
const pctY = (y: number) => (y / FRAME_HEIGHT) * 100;

// Card grid coordinates (relative to the grid wrapper at 960,1220.78, w=892.66)
const GRID_LEFT = 960;
const GRID_TOP = 1220.78;
const GRID_WIDTH = 892.66;
const GRID_HEIGHT = 774.52;
const COL_X = [0, 447.1] as const;
const ROW_Y = [0, 302.42, 603.97] as const;
const ROW_HEIGHT = 170.55;
const COL_WIDTH = 357.09;
const CARD_TITLE_Y = 20.18;
const CARD_BODY_Y = 66.55;

export const B2BMediaProgram = ({ section, assets }: B2BMediaProgramProps) => {
    const heroSrc = section.heroAssetKey
        ? assets[section.heroAssetKey]
        : undefined;
    const heroWebmSrc = section.heroWebmAssetKey
        ? assets[section.heroWebmAssetKey]
        : undefined;
    const filmSrc = section.filmCoverAssetKey
        ? assets[section.filmCoverAssetKey]
        : undefined;

    // When a hero link is set, wrap the hero media in an external anchor so
    // clicking the (muted, autoplaying) teaser opens it on YouTube with sound.
    const withHeroLink = (media: ReactNode, className: string): ReactNode =>
        section.heroLink ? (
            <a
                href={section.heroLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Watch ArchiPro on YouTube"
                className={`${className} cursor-pointer`}
            >
                {media}
            </a>
        ) : (
            media
        );

    return (
        <section
            className="relative w-full bg-white max-md:bg-transparent"
            style={{ aspectRatio: `1920 / ${FRAME_HEIGHT}` }}
        >
            {/* Desktop layout — absolute-positioned to the 1920 frame */}
            <div className="absolute inset-0 max-md:hidden">
                {/* Top hero photo / video (1870 × 915.94) */}
                {heroSrc && (
                    <div
                        className="absolute overflow-hidden"
                        style={{
                            left: `${pctX(25)}%`,
                            top: `${pctY(-0.02)}%`,
                            width: `${pctX(1870)}%`,
                            height: `${pctY(915.94)}%`,
                        }}
                    >
                        {withHeroLink(
                            section.heroIsVideo ? (
                                <video
                                    className="absolute inset-0 size-full object-cover"
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                >
                                    {heroWebmSrc && (
                                        <source
                                            src={heroWebmSrc}
                                            type="video/webm"
                                        />
                                    )}
                                    <source src={heroSrc} type="video/mp4" />
                                </video>
                            ) : (
                                <img
                                    className="absolute inset-0 size-full object-cover"
                                    src={heroSrc}
                                    alt=""
                                />
                            ),
                            'absolute inset-0 block',
                        )}
                    </div>
                )}

                {/* Right intro paragraph (left=960, top=1013.5, w=865.66) */}
                <p
                    className="absolute font-sans text-[1.302vw] font-medium leading-[1.1] tracking-[-0.030em] text-[#000] [word-break:break-word]"
                    style={{
                        left: `${pctX(960)}%`,
                        top: `${pctY(1013.5)}%`,
                        width: `${pctX(865.66)}%`,
                    }}
                >
                    {section.body}
                </p>

                {/* Film cover card (left=213, top=1133, w=492.61, h=680.62).
                 * Flat on the page bg — no drop shadow in Figma. */}
                {filmSrc && (
                    <div
                        className="absolute overflow-hidden bg-white"
                        style={{
                            left: `${pctX(213)}%`,
                            top: `${pctY(1133)}%`,
                            width: `${pctX(492.61)}%`,
                            height: `${pctY(680.62)}%`,
                        }}
                    >
                        {section.filmIsVideo ? (
                            <video
                                className="absolute inset-0 size-full object-cover object-bottom"
                                src={filmSrc}
                                autoPlay
                                muted
                                loop
                                playsInline
                            />
                        ) : (
                            <img
                                className="absolute inset-0 size-full object-cover object-bottom"
                                src={filmSrc}
                                alt=""
                            />
                        )}
                    </div>
                )}

                {/* Card grid wrapper (left=960, top=1220.78, w=892.66, h=774.52) */}
                <div
                    className="absolute"
                    style={{
                        left: `${pctX(GRID_LEFT)}%`,
                        top: `${pctY(GRID_TOP)}%`,
                        width: `${pctX(GRID_WIDTH)}%`,
                        height: `${pctY(GRID_HEIGHT)}%`,
                    }}
                >
                    {ROW_Y.map((rowYValue, rowIdx) => {
                        const leftCard = section.cards[rowIdx * 2];
                        const rightCard = section.cards[rowIdx * 2 + 1];
                        const rowTop = (rowYValue / GRID_HEIGHT) * 100;
                        const rowH = (ROW_HEIGHT / GRID_HEIGHT) * 100;
                        return (
                            <div
                                key={rowIdx}
                                className="absolute left-0 right-0"
                                style={{
                                    top: `${rowTop}%`,
                                    height: `${rowH}%`,
                                }}
                            >
                                {/* Top divider line */}
                                <div
                                    aria-hidden="true"
                                    className="absolute left-0 right-0 top-0 h-px bg-[#181818]/15"
                                />
                                {COL_X.map((colXValue, colIdx) => {
                                    const card =
                                        colIdx === 0 ? leftCard : rightCard;
                                    if (!card) return null;
                                    return (
                                        <div
                                            key={colIdx}
                                            className="absolute"
                                            style={{
                                                left: `${(colXValue / GRID_WIDTH) * 100}%`,
                                                top: 0,
                                                width: `${(COL_WIDTH / GRID_WIDTH) * 100}%`,
                                                height: '100%',
                                            }}
                                        >
                                            <p
                                                className="absolute left-0 right-0 font-sans text-[1.042vw] font-medium leading-[1.3] tracking-[-0.030em] text-[#333]"
                                                style={{
                                                    top: `${(CARD_TITLE_Y / ROW_HEIGHT) * 100}%`,
                                                }}
                                            >
                                                {card.title}
                                            </p>
                                            {card.body && (
                                                <p
                                                    className="absolute left-0 right-0 font-sans text-[1.042vw] font-medium leading-[1.3] tracking-[-0.030em] text-[#888] [word-break:break-word]"
                                                    style={{
                                                        top: `${(CARD_BODY_Y / ROW_HEIGHT) * 100}%`,
                                                    }}
                                                >
                                                    {card.body}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Mobile fallback — stacked column with Phase-2 vw tokens.
             * Intro 3.255 vw, card titles 3.65 vw, card body 3.255 vw,
             * 5.4 % gutter, 5 vw gaps. (The "(ArchiPro Media Program)"
             * eyebrow lives on the preceding intro-band section, not on
             * this component, so no eyebrow renders here.) */}
            <div className="flex flex-col gap-[5vw] bg-white px-[5.4%] md:hidden">
                {heroSrc &&
                    withHeroLink(
                        section.heroIsVideo ? (
                            <video
                                className="aspect-[1870/916] w-full object-cover"
                                autoPlay
                                muted
                                loop
                                playsInline
                            >
                                {heroWebmSrc && (
                                    <source
                                        src={heroWebmSrc}
                                        type="video/webm"
                                    />
                                )}
                                <source src={heroSrc} type="video/mp4" />
                            </video>
                        ) : (
                            <img
                                className="aspect-[1870/916] w-full object-cover"
                                src={heroSrc}
                                alt=""
                            />
                        ),
                        'block w-full',
                    )}
                <p className="font-sans text-[3.255vw] font-medium leading-[1.1] tracking-[-0.030em] text-[#181818]">
                    {section.body}
                </p>
                {filmSrc && (
                    <img
                        className="aspect-[492/680] w-full self-center object-cover object-bottom"
                        src={filmSrc}
                        alt=""
                    />
                )}
                <div className="grid grid-cols-1 gap-[5vw]">
                    {section.cards.map((card) => (
                        <div
                            key={card.title}
                            className="flex flex-col gap-[1.5vw] border-t border-[#181818]/15 pt-[2vw]"
                        >
                            <h3 className="font-sans text-[3.65vw] leading-[1.07] tracking-[-0.044em] text-[#333]">
                                {card.title}
                            </h3>
                            {card.body && (
                                <p className="font-sans text-[3.255vw] font-medium leading-[1.1] tracking-[-0.030em] text-[#888]">
                                    {card.body}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};
